import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Alert, Box, Button, IconButton, Radio, TextField, Tooltip, Typography } from '@mui/material';
import { map } from 'es-toolkit/compat';
import React from 'react';

import { SpotDetectionCountBin } from 'interfaces/calculateFeatures';
import {
  normalizeSpotDetectionCountBins,
  normalizeSpotDetectionPositivityThresholdScore,
} from '../../../spotDetectionInputSetup';
import { useHScoreBins } from './useHScoreBins';

interface HScoreBinsEditorProps {
  countBins: SpotDetectionCountBin[];
  onChange: (countBins: SpotDetectionCountBin[]) => void;
  positivityThresholdScore?: number;
  onPositivityThresholdScoreChange: (positivityThresholdScore: number) => void;
  showPositivityThreshold?: boolean;
  maximumDrafts?: string[];
  onMaximumDraftsChange?: (drafts: string[]) => void;
  editableFirstMinimum?: boolean;
}

const rowGridSx = {
  display: 'grid',
  gridTemplateColumns: '72px minmax(104px, 1fr) minmax(128px, 1fr) 152px 40px',
  gap: 1,
  alignItems: 'center',
};

export const HScoreBinsEditor: React.FC<HScoreBinsEditorProps> = ({
  countBins,
  onChange,
  positivityThresholdScore,
  onPositivityThresholdScoreChange,
  showPositivityThreshold = true,
  maximumDrafts,
  onMaximumDraftsChange,
  editableFirstMinimum = false,
}) => {
  const {
    rows,
    error,
    invalidScore,
    canAddScore,
    canRemoveScore,
    updateMaximum,
    updateFirstMinimum,
    addScore,
    removeScore,
  } = useHScoreBins({ countBins, onChange, controlledMaximumDrafts: maximumDrafts, onMaximumDraftsChange });
  const normalizedPositivityThresholdScore = normalizeSpotDetectionPositivityThresholdScore(
    positivityThresholdScore,
    normalizeSpotDetectionCountBins(countBins)
  );

  const effectiveRowGridSx = {
    ...rowGridSx,
    gridTemplateColumns: showPositivityThreshold
      ? rowGridSx.gridTemplateColumns
      : '72px minmax(104px, 1fr) minmax(128px, 1fr) 40px',
  };

  return (
    <Box sx={{ mt: 1, maxWidth: 640 }}>
      <Box sx={{ ...effectiveRowGridSx, px: 0.5, mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          Score
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          Minimum spots
        </Typography>
        <Typography variant="caption" color="text.secondary" fontWeight={700}>
          Maximum spots
        </Typography>
        {showPositivityThreshold && (
          <Typography variant="caption" color="text.secondary" fontWeight={700}>
            Positivity threshold
          </Typography>
        )}
      </Box>

      {map(rows, ({ score, minimum, maximumDraft }) => {
        const isOpenEnded = maximumDraft === null;

        return (
          <Box key={score} sx={{ ...effectiveRowGridSx, minHeight: 48, px: 0.5 }}>
            <Typography component="strong" variant="body2" fontWeight={700}>
              Score {score}
            </Typography>
            <TextField
              size="small"
              value={minimum ?? ''}
              type={score === 1 && editableFirstMinimum ? 'number' : 'text'}
              inputProps={{
                readOnly: !(score === 1 && editableFirstMinimum),
                min: 1,
                step: 1,
                'aria-label': `Score ${score} minimum spots`,
              }}
              onChange={
                score === 1 && editableFirstMinimum ? (event) => updateFirstMinimum(event.target.value) : undefined
              }
            />
            <TextField
              size="small"
              type={isOpenEnded ? 'text' : 'number'}
              value={isOpenEnded ? 'No maximum' : maximumDraft}
              error={!isOpenEnded && invalidScore === score}
              inputProps={{
                readOnly: isOpenEnded,
                min: score === 0 ? 0 : undefined,
                step: isOpenEnded ? undefined : 1,
                'aria-label': `Score ${score} maximum spots`,
              }}
              onChange={
                isOpenEnded
                  ? undefined
                  : (event) =>
                      score === 0 && editableFirstMinimum
                        ? updateFirstMinimum(
                            event.target.value.trim() === '' ? '' : String(Number(event.target.value) + 1)
                          )
                        : updateMaximum(score, event.target.value)
              }
            />
            {showPositivityThreshold &&
              (score > 0 ? (
                <Radio
                  size="small"
                  name="positivity-threshold-score"
                  checked={normalizedPositivityThresholdScore === score}
                  onChange={() => onPositivityThresholdScoreChange(score)}
                  inputProps={{ 'aria-label': `Use Score ${score} as the positivity threshold` }}
                />
              ) : (
                <span />
              ))}
            {score > 0 ? (
              <Tooltip title={`Remove Score ${score}`}>
                <span>
                  <IconButton
                    size="small"
                    aria-label={`Remove Score ${score}`}
                    disabled={!canRemoveScore}
                    onClick={() => removeScore(score)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : (
              <span />
            )}
          </Box>
        );
      })}

      <Button size="small" startIcon={<AddIcon />} sx={{ mt: 0.5 }} disabled={!canAddScore} onClick={addScore}>
        Add score
      </Button>
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};
